import { useRouter } from 'next/router'
import { Fragment, useCallback, useState } from 'react'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'

import { useAuth } from 'src/providers/AuthProvider'
import { useMenuState } from 'src/providers/MenuStateProvider'
import { useUser } from 'src/providers/UserProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Divider } from 'src/components/atoms/Divider'
import { InternalLink, OnClickLink } from 'src/components/atoms/Link'
import { Switch } from 'src/components/atoms/Switch'
import { MenuSectionTitle } from 'src/components/atoms/Typography'
import { AccountInfo } from 'src/components/molecules/AccountInfo'
import { MenuItem, SubMenu } from 'src/components/molecules/MenuDropdown'
import { UserInfo } from 'src/components/molecules/UserInfo'

import { useListRegistriesQuery } from 'src/config/generated/graphql'
import {
  bulkGiftPath,
  dashboardPath,
  donatePath,
  giftPath,
  listMyRegistriesPath,
  listRegistriesPath,
  planterDashboardPath,
  viewAccountDetailPath
} from 'src/config/paths'
import { ActiveProfileType, NavigationContext } from 'src/types/account'

import { LinkOption, LinkOptionType } from '../../types'

const userActions: {
  label: string
  path: string
}[] = [
  {
    label: 'Donate',
    path: donatePath()
  },
  {
    label: 'Gift',
    path: giftPath()
  },
  {
    label: 'Bulk gift',
    path: bulkGiftPath()
  },
  {
    label: 'Tree registries',
    path: listRegistriesPath
  }
]

export type MobileAccountMenuProps = {
  links: LinkOption[]
  navigationContext: NavigationContext
}

export const MobileAccountMenu = ({
  links,
  navigationContext
}: MobileAccountMenuProps) => {
  const { isStaff, isAdvisor, logout, login } = useAuth()
  const { isAdminMode, setAdminMode, user } = useUser()
  const { data: registriesData } = useListRegistriesQuery({
    variables: {
      funderId: user?.Account?.FunderId,
      offset: 0,
      limit: 1
    }
  })

  const [expandedMenuItem, setExpandedMenuItem] = useState('')
  const handleChangeAdminToggle = () => {
    setAdminMode(!isAdminMode)
  }
  const { setIsOpen } = useMenuState()
  const router = useRouter()

  const renderLink = useCallback(
    (link: LinkOption, index: number) => {
      if (link.options?.length) {
        return (
          <Fragment key={`link_${index}`}>
            <OnClickLink
              color='black'
              className='block w-full'
              onClick={() => {
                setExpandedMenuItem(prev => {
                  if (prev === link.label) return ''
                  return link.label
                })
              }}
            >
              <div className='flex justify-between cursor-pointer text-nowrap items-center text-[16px] font-medium uppercase px-5 py-3.25 hover:bg-frostWhite'>
                {link.label}
                <span>
                  {expandedMenuItem === link.label ? (
                    <MdKeyboardArrowUp size='20px' />
                  ) : (
                    <MdKeyboardArrowDown size='20px' />
                  )}
                </span>
              </div>
            </OnClickLink>
            {expandedMenuItem === link.label ? (
              <div>
                {link.options?.map((option, i) => {
                  if (option.type === LinkOptionType.Seperator)
                    return <div key={i} />
                  return (
                    <MenuItem
                      key={i}
                      option={option}
                      onClick={() => {
                        setIsOpen(false)
                      }}
                    />
                  )
                })}
                {link.subMenu && link.subMenu.subMenuOptions?.length ? (
                  <SubMenu subMenu={link.subMenu} />
                ) : null}
              </div>
            ) : null}
          </Fragment>
        )
      }

      if (!link.path) return null
      return (
        <div
          key={`link_${index}`}
          className='text-nowrap text-[16px] font-medium uppercase px-5 py-3.25 hover:bg-frostWhite '
        >
          <InternalLink
            className='block'
            href={link.path!}
            color='black'
            highlightActive
            onClick={() => setExpandedMenuItem('')}
          >
            {link.label}
          </InternalLink>
        </div>
      )
    },
    [expandedMenuItem, setIsOpen]
  )

  return (
    <div className='z-0 **:text-forestDeep! bg-linen shadow-[0px_4px_8px_2px_rgba(51,51,51,0.15)]'>
      {/* admin mode switch */}
      {isStaff || isAdvisor ? (
        <>
          <div className='space-x-4 p-5 text-[16px] font-medium'>
            <span>{isAdminMode ? 'Admin' : 'App'}</span>
            <Switch
              title='Admin'
              checked={isAdminMode}
              onChange={handleChangeAdminToggle}
            />
          </div>
          <Divider className='m-0!' />
        </>
      ) : null}
      <div className='p-5'>
        <UserInfo />
        {user && navigationContext === NavigationContext.Public ? (
          <ButtonRebrand
            variant='lime'
            className='mt-3.25'
            onClick={() => {
              setIsOpen(false)
              router.push(
                user.ActiveProfileType === ActiveProfileType.Planter
                  ? planterDashboardPath()
                  : dashboardPath()
              )
            }}
          >
            Account
          </ButtonRebrand>
        ) : null}
      </div>

      <div id='MENULINKS'>{links.map(renderLink)}</div>
      <Divider className='m-0!' />
      {navigationContext !== NavigationContext.Public ? null : (
        <>
          <div className='flex flex-col pt-5 pb-2.5'>
            <MenuSectionTitle className='pb-2.5 px-5'>
              Fund native trees
            </MenuSectionTitle>
            {userActions.map(action => {
              return (
                <InternalLink
                  key={action.label}
                  className='hover:bg-frostWhite px-5 py-3.25 uppercase'
                  color='black'
                  href={action.path}
                  highlightActive
                >
                  <div className='flex flex-row gap-2.5'>
                    <span>{action.label}</span>
                  </div>
                </InternalLink>
              )
            })}
          </div>
          <Divider className='m-0!' />
        </>
      )}
      {user?.Account && navigationContext !== NavigationContext.Public && (
        <>
          <div className='p-5'>
            <AccountInfo />
          </div>
          <Divider className='m-0!' />
        </>
      )}
      {user && navigationContext !== NavigationContext.Public && (
        <>
          {user.Account &&
          user.ActiveProfileType === ActiveProfileType.Funder ? (
            <>
              {registriesData?.registries.length ? (
                <InternalLink
                  className='hover:bg-frostWhite py-3.25 px-5 flex flex-row justify-between items-center uppercase'
                  color='black'
                  highlightActive
                  href={listMyRegistriesPath}
                >
                  Your tree registries
                </InternalLink>
              ) : null}
              <InternalLink
                className='hover:bg-frostWhite py-3.25 px-5 flex flex-row justify-between items-center uppercase'
                color='black'
                highlightActive
                href={viewAccountDetailPath}
              >
                Funder history
              </InternalLink>
            </>
          ) : null}
          <InternalLink
            className='hover:bg-frostWhite py-3.25 px-5 flex flex-row justify-between items-center uppercase'
            color='black'
            highlightActive
            href={viewAccountDetailPath}
          >
            Account settings
          </InternalLink>
          <Divider className='m-0!' />
        </>
      )}
      {user ? (
        <OnClickLink
          color='black'
          className='hover:bg-frostWhite py-3.25 px-5 flex flex-row justify-between items-center uppercase'
          onClick={logout}
        >
          Log out
        </OnClickLink>
      ) : (
        <OnClickLink
          color='black'
          className='hover:bg-frostWhite py-3.25 px-5 flex flex-row justify-between items-center uppercase'
          onClick={login}
        >
          Log in
        </OnClickLink>
      )}
    </div>
  )
}
